import type { FC, ReactNode, RefObject } from 'react';

import { Drawer } from '@base-ui/react/drawer';
import { Typography } from '@mui/material';
import { ChevronDownDashboardIcon, CloseIcon } from '@truvhq/ui';
import { clsx } from 'clsx';

import { useKeyboardFrame } from 'shared/lib/screen/useKeyboardFrame';

type Props = {
  open: boolean;
  title: string;
  /**
   * Accessible name for the close control. The sheet is already named by its title, so the default
   * is enough wherever that title says what is being closed — name it only when the title is
   * generic, as "Actions" is.
   */
  closeLabel?: string;
  /** Drop the close control where dismissing should go through an action instead */
  withCloseIcon?: boolean;
  onClose: VoidFunction;
  /** Turns the sheet into a sub-screen: the left slot navigates back instead of staying empty */
  onBack?: VoidFunction;
  /** Buttons pinned under the body, clear of the home indicator. Pass them bare and in the order a
   * dialog would lay them out left to right — the sheet stacks and stretches them itself, and
   * reverses them so the commit action a desktop puts rightmost ends up on top. */
  footer?: ReactNode;
  /** The scroll root, for a body that pages in more items as it nears the bottom */
  bodyRef?: RefObject<HTMLDivElement>;
  /** For a body that lays out and scrolls its own regions — a chat panel, say — rather than being a
   * column of content the sheet pads and scrolls for it */
  flush?: boolean;
  /** Sheet classes, normally a height the default cap is too generous for */
  className?: string;
  /**
   * Take the whole height on offer instead of the content's. For a body whose height comes and goes
   * — a list that empties while the next query is in flight — which would otherwise take the sheet
   * with it.
   */
  fill?: boolean;
  /**
   * Rendered under the title and outside the scroll area. A search field belongs here: the keyboard
   * is aligned against the sheet's fixed frame, which only holds if the field is part of it rather
   * than of the body that scrolls.
   */
  sticky?: ReactNode;
  /** Keeps the body alive while the sheet is closed, for one that holds a session of its own */
  keepMounted?: boolean;
  children: ReactNode;
};

/**
 * The panel that rises from the bottom edge where a desktop would open a popover or a dialog, and
 * that you can throw away with your thumb.
 *
 * Built on Base UI's drawer rather than MUI's, which has no gesture at all: a pull on a MUI sheet
 * reaches the page behind it, and with a keyboard up that scroll leaves the sheet stranded in the
 * few visible pixels above it, too short to use and with no way out but the close button. Here the
 * same pull is a dismissal. Its height is useKeyboardFrame's business; see the notes there, and in
 * app/styles/style.css for what the sheet can and cannot reach on iOS.
 */
export const SwipeSheet: FC<Props> = ({
  open,
  title,
  closeLabel = 'Close',
  withCloseIcon = true,
  onClose,
  onBack,
  footer,
  bodyRef,
  flush = false,
  className,
  fill = false,
  sticky,
  keepMounted,
  children,
}) => {
  useKeyboardFrame(open);

  return (
    <Drawer.Root
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <Drawer.VirtualKeyboardProvider>
        <Drawer.Portal keepMounted={keepMounted}>
          {/* Base UI leaves out a nested drawer's backdrop, so a sheet opened from inside another
              would sit on it with nothing between them to mark the one underneath as inactive */}
          <Drawer.Backdrop forceRender className="swipe-sheet-backdrop" />
          <Drawer.Viewport className="swipe-sheet-viewport">
            <Drawer.Popup className={clsx('swipe-sheet-popup', fill && 'swipe-sheet-popup-fill', className)}>
              {/* The sheet is dragged by its header, so everything that is not the body lives here.
                  Equal side tracks keep the title optically centered, as in PageHeader; the
                  controls pin to the top of the row rather than its middle, so nothing moves under
                  a one-line title and a title that wraps grows downward past them. */}
              <div className="swipe-sheet-header px-4 pt-4">
                <div className="grid grid-cols-[minmax(min-content,1fr)_auto_minmax(min-content,1fr)] items-center gap-2 pb-2">
                  <div className="flex min-h-8 items-center self-start justify-self-start">
                    {onBack && (
                      <button
                        type="button"
                        className="-ml-1 flex cursor-pointer items-center bg-transparent text-primary-main"
                        onClick={onBack}
                      >
                        <ChevronDownDashboardIcon className="rotate-90" />
                        <Typography variant="body1">Back</Typography>
                      </button>
                    )}
                  </div>
                  {/* `text-center` only shows once a title wraps: a single-line one already sits in
                      a track exactly as wide as itself */}
                  <Drawer.Title
                    render={
                      <Typography className="text-center" component="h2" variant="h2">
                        {title}
                      </Typography>
                    }
                  />
                  {withCloseIcon && (
                    <Drawer.Close
                      aria-label={closeLabel}
                      className="flex h-8 w-8 cursor-pointer items-center justify-center self-start justify-self-end rounded-full border-0 bg-grey20 p-0 text-grey70"
                    >
                      <CloseIcon height={20} width={20} />
                    </Drawer.Close>
                  )}
                </div>
                {/* Dragging inside a field is the caret's business, not the sheet's */}
                {!!sticky && <div data-base-ui-swipe-ignore>{sticky}</div>}
              </div>

              <Drawer.Content
                ref={bodyRef}
                className={clsx('swipe-sheet-body', flush ? 'swipe-sheet-body-flush' : 'px-4')}
              >
                {children}
              </Drawer.Content>

              {!!footer && (
                <div className="swipe-sheet-header flex flex-col-reverse gap-2 px-4 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] [&>*]:w-full">
                  {footer}
                </div>
              )}
            </Drawer.Popup>
          </Drawer.Viewport>
        </Drawer.Portal>
      </Drawer.VirtualKeyboardProvider>
    </Drawer.Root>
  );
};
